import { type DragEvent, type FC, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { Colors, Control } from "@snapchat/snap-design-system";
import {
    CheckIcon,
    CrossIcon,
    ErrorIcon,
    GalleryImageIcon,
    MediaIcon,
    Sizes,
    UploadIcon,
} from "@snapchat/snap-design-system-icons";

import { formatSize } from "../utils/formatSize";

import {
    DropZone,
    DropZoneContent,
    DropZoneFileRow,
    DropZoneHint,
    DropZoneIconTile,
    DropZonePrompt,
    DropZoneSize,
    DropZoneThumb,
    DropZoneTrigger,
    ErrorWrapper,
    RemoveButton,
    hideInput,
} from "./AssetSubmission.styles";
import { DragDropPrompt, RemoveFile } from "./messages.generated";

interface FileDropZoneProps {
    label: ReactNode;
    hint: ReactNode;
    variant: "asset" | "thumbnail";
    file?: File;
    accept?: string;
    error?: ReactNode;
    onSelect: (file: File) => void;
    onRemove: () => void;
    className?: string;
    name?: string;
}

export const FileDropZone: FC<FileDropZoneProps> = ({
    label,
    hint,
    variant,
    file,
    accept,
    error,
    onSelect,
    onRemove,
    className,
    name,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [thumbUrl, setThumbUrl] = useState<string>();

    const hasFile = Boolean(file?.name);
    const hasError = Boolean(error);

    useEffect(() => {
        if (variant === "thumbnail" && file instanceof File && file.name && !error) {
            const url = URL.createObjectURL(file);
            setThumbUrl(url);
            return () => {
                URL.revokeObjectURL(url);
            };
        }
        setThumbUrl(undefined);
        return;
    }, [file, error, variant]);

    const handleFiles = useCallback(
        (files: FileList | null) => {
            const selected = files?.[0];
            if (selected) onSelect(selected);
        },
        [onSelect],
    );

    const onDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setDragOver(false);
            handleFiles(event.dataTransfer.files);
        },
        [handleFiles],
    );

    const state = dragOver ? "dragover" : hasError ? "error" : hasFile ? "filled" : "idle";

    return (
        <Control label={label} className={className}>
            <input
                ref={inputRef}
                type="file"
                name={name}
                className={hideInput}
                onChange={(event) => {
                    handleFiles(event.target.files);
                    // Reset so selecting the same file again re-fires change
                    // (e.g. after removal or a validation rejection).
                    event.target.value = "";
                }}
                accept={accept}
            />
            <DropZone
                $state={state}
                onDragOver={(event) => {
                    event.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => {
                    setDragOver(false);
                }}
                onDrop={onDrop}
            >
                <DropZoneTrigger
                    type="button"
                    onClick={() => {
                        inputRef.current?.click();
                    }}
                >
                    <span className={hideInput}>
                        <DragDropPrompt />
                    </span>
                </DropZoneTrigger>
                {hasFile && !hasError ? (
                    <>
                        {variant === "thumbnail" && thumbUrl ? (
                            <DropZoneThumb alt={file?.name} src={thumbUrl} />
                        ) : (
                            <DropZoneIconTile $filled>
                                <MediaIcon size={Sizes.Large} />
                            </DropZoneIconTile>
                        )}
                        <DropZoneContent>
                            <DropZoneFileRow title={file?.name}>
                                <CheckIcon fill={Colors.Green.V200} size={Sizes.Medium} />
                                <span className="filename">{file?.name}</span>
                            </DropZoneFileRow>
                            <DropZoneSize>{formatSize(file?.size ?? 0)}</DropZoneSize>
                        </DropZoneContent>
                        <RemoveButton
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                onRemove();
                            }}
                        >
                            <CrossIcon size={Sizes.Medium} />
                            <span className={hideInput}>
                                <RemoveFile />
                            </span>
                        </RemoveButton>
                    </>
                ) : (
                    <>
                        <DropZoneIconTile>
                            {variant === "thumbnail" ? (
                                <GalleryImageIcon size={Sizes.Large} />
                            ) : (
                                <UploadIcon size={Sizes.Large} />
                            )}
                        </DropZoneIconTile>
                        <DropZoneContent>
                            <DropZonePrompt>
                                <DragDropPrompt />
                            </DropZonePrompt>
                            <DropZoneHint>{hint}</DropZoneHint>
                        </DropZoneContent>
                    </>
                )}
            </DropZone>
            {hasError && (
                <ErrorWrapper>
                    <ErrorIcon fill={Colors.Red.V200} />
                    <span className="sds-control-status error">{error}</span>
                </ErrorWrapper>
            )}
        </Control>
    );
};
